import type { NotificationPreferenceConfig } from '../types/notification';

export interface AttentionState {
  /** Item carries server permission for the channel. */
  itemToast: boolean;
  itemSound: boolean;
  /** Current client settings (may only narrow the server decision). */
  toastEnabled: boolean;
  soundEnabled: boolean;
  /** User preference: mute while studying (only bites when studying is true). */
  mutedWhileStudying: boolean;
  /** Whether the user is REALLY on a study/practice/submission route right now. */
  studying: boolean;
  quietActive: boolean;
  snoozeActive: boolean;
  /** Tab/document visibility. */
  visible: boolean;
  /** Age of the item in seconds (historical items must stay silent). */
  ageSeconds: number;
  /** A burst signature (type+classroom) already alerted within the window. */
  burstSuppressed?: boolean;
  /**
   * The sound allowance for this signature was already used. Tracked separately
   * from `burstSuppressed`: a toast-only alert must not silence a later row of
   * the same signature that the policy allowed to sound.
   */
  soundSuppressed?: boolean;
}

export interface AttentionDecision {
  toast: boolean;
  sound: boolean;
  burstSummary: boolean;
}

export const HISTORICAL_SECONDS = 120;

/** Routes where the user is actively practicing/submitting and can be muted. */
const STUDYING_ROUTE_PATTERNS = [/\/practice(\/|$)/, /\/submission(\/|$)/, /\/sessions\/detail(\/|$)/];

/**
 * Whether a pathname is an active study/practice/submission route. Pure so it
 * can be unit-tested with a fixed path; defaults to NOT studying when there is
 * no signal, so the mute-while-studying preference never silences the app by
 * accident on ordinary pages.
 */
export function isStudyingRoute(pathname: string | null | undefined): boolean {
  if (typeof pathname !== 'string' || pathname.length === 0) return false;
  return STUDYING_ROUTE_PATTERNS.some((pattern) => pattern.test(pathname));
}

/**
 * Pure attention policy: given an item's server-side permissions and current
 * client settings, decide whether to show a toast / play a sound. It can only
 * narrow what the server already allowed — a notification suppressed at fanout
 * time never gets its channel re-opened by a later setting change. Historical
 * items (older than 120s) and non-visible tabs stay silent.
 */
export function decideAttention(state: AttentionState): AttentionDecision {
  const backgroundSuppressed =
    !state.visible ||
    state.quietActive ||
    state.snoozeActive ||
    state.ageSeconds > HISTORICAL_SECONDS;

  // The preference only applies while the user really is studying.
  const studyingMuted = state.mutedWhileStudying && state.studying;

  const toast =
    !backgroundSuppressed &&
    state.itemToast &&
    state.toastEnabled &&
    !studyingMuted &&
    !state.burstSuppressed;

  const sound =
    !backgroundSuppressed &&
    state.itemSound &&
    state.soundEnabled &&
    !studyingMuted &&
    !state.soundSuppressed;

  return { toast, sound, burstSummary: Boolean(state.burstSuppressed) };
}

/** Whether the tab is currently visible (document API guarded for tests). */
export function isDocumentVisible(): boolean {
  if (typeof document === 'undefined') return false;
  return document.visibilityState === 'visible';
}

/** Minutes since midnight for a `HH:mm` string, or null when unparseable. */
export function parseClockMinutes(value: string): number | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(String(value ?? '').trim());
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;
  return hours * 60 + minutes;
}

/**
 * Local wall-clock minutes for an instant in an IANA timezone. Falls back to
 * the host's local time when the timezone is missing/invalid or Intl is
 * unavailable, so a bad config never mutes notifications forever.
 */
export function clockMinutesInTimeZone(now: Date, timeZone?: string | null): number {
  if (timeZone) {
    try {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone,
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }).formatToParts(now);
      const hour = parts.find((p) => p.type === 'hour')?.value;
      const minute = parts.find((p) => p.type === 'minute')?.value;
      if (hour != null && minute != null) {
        // `hour12: false` can render midnight as 24 in some engines.
        return (Number(hour) % 24) * 60 + Number(minute);
      }
    } catch {
      // Unknown/invalid IANA zone: fall through to local time.
    }
  }
  return now.getHours() * 60 + now.getMinutes();
}

/**
 * Whether `now` falls inside a start/end wall-clock window, evaluated in the
 * configured IANA timezone. Overnight windows (start > end) wrap midnight.
 * An unknown timezone falls back to the host's local time.
 */
export function inQuietWindow(
  start: string,
  end: string,
  now: Date,
  timeZone?: string | null,
): boolean {
  const startMin = parseClockMinutes(start);
  const endMin = parseClockMinutes(end);
  if (startMin == null || endMin == null || startMin === endMin) return false;
  const nowMin = clockMinutesInTimeZone(now, timeZone);
  if (startMin < endMin) return nowMin >= startMin && nowMin < endMin;
  return nowMin >= startMin || nowMin < endMin;
}

/** Compute quiet/snooze activity from a preference config at a given instant. */
export function isQuietOrSnoozed(
  config: NotificationPreferenceConfig,
  now: Date,
): { quiet: boolean; snooze: boolean } {
  const snooze = config.snoozedUntil != null && new Date(config.snoozedUntil) > now;
  let quiet = false;
  if (config.quietHours?.enabled) {
    quiet = inQuietWindow(
      config.quietHours.start,
      config.quietHours.end,
      now,
      config.quietHours.timezone,
    );
  }
  return { quiet, snooze };
}
